// Layer 3: Picto Type Layer
// Applies final picto appearance based on type (resource status, booking pin, user avatar)

import { PictoTypeEnum } from '../../components';
import { IPictoConfiguration } from './pictoSizeLayer';
import { ResourceStateDtoStatusEnum } from '@jooxter/api';

export interface IResourcePictoData {
  id: number;
  name: string;
  pictogram: string;
  showMapIcon: boolean;
  isFavorite: boolean | undefined;
  status: ResourceStateDtoStatusEnum | undefined;
  capacity: number;
  occupantsCount: number | undefined;
  subtitle: string;
  heatmapIntensity?: number;
}

export interface IUserPictoData {
  firstName: string;
  lastName: string;
  pictureUrl?: string;
}

export interface IFinalPicto {
  id: number;
  x: number;
  y: number;
  priority: number;
  size: number;
  type: PictoTypeEnum;
  url: string;
  showMapIcon: boolean;

  // Resource-specific
  title?: string;
  subtitle?: string;
  isFavorite?: boolean;
  status?: ResourceStateDtoStatusEnum;
  capacity?: number;
  occupantsCount?: number;
  heatmapIntensity?: number;

  // User-specific
  userInitials?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Layer 3: Picto Type Layer
 *
 * Applies final styling and appearance based on picto type:
 * - Resource: status-based pictogram
 * - Booking: blue location dot
 * - User: profile photo or initials
 */
export class PictoTypeLayer {
  /**
   * Applies resource type styling with status-based pictograms
   */
  static applyResourceType(
    configurations: IPictoConfiguration[],
    resourceData: Map<number, IResourcePictoData>
  ): IFinalPicto[] {
    const pictos: IFinalPicto[] = [];

    for (const config of configurations) {
      const data = resourceData.get(config.id);
      if (!data?.pictogram) {
        continue;
      }

      pictos.push({
        id: config.id,
        x: config.finalX,
        y: config.finalY,
        size: config.size,
        type: PictoTypeEnum.Resource,
        url: `images/${data.pictogram.toLowerCase()}.svg`,
        showMapIcon: data.showMapIcon,
        priority: 1,
        title: data.name,
        subtitle: data.subtitle,
        isFavorite: data.isFavorite,
        status: data.status,
        capacity: data.capacity,
        occupantsCount: data.occupantsCount,
        heatmapIntensity: data.heatmapIntensity,
      });
    }

    return pictos;
  }

  /**
   * Applies booking type styling with location dot
   */
  static applyBookingType(configurations: IPictoConfiguration[], locationDotUrl: string): IFinalPicto[] {
    return configurations.map((config) => ({
      id: config.id,
      x: config.finalX,
      y: config.finalY,
      priority: 1,
      size: config.size,
      type: PictoTypeEnum.Booking,
      url: locationDotUrl,
      showMapIcon: true,
    }));
  }

  /**
   * Applies user type styling with profile photo or initials
   */
  static applyUserType(configurations: IPictoConfiguration[], userData: Map<number, IUserPictoData>): IFinalPicto[] {
    return configurations
      .map((config) => {
        const data = userData.get(config.id);
        if (!data) {
          return;
        }

        const userInitials = `${data.firstName[0]?.toUpperCase() ?? ''}${data.lastName[0]?.toUpperCase() ?? ''}`;
        return {
          id: config.id,
          x: config.finalX,
          y: config.finalY,
          size: config.size,
          priority: 1,
          type: PictoTypeEnum.User,
          url: data.pictureUrl ?? '',
          showMapIcon: true,
          userInitials,
          firstName: data.firstName,
          lastName: data.lastName,
        };
      })
      .filter((d) => !!d);
  }

  /**
   * Applies resource type styling with heatmap intensity-based pictograms
   */
  static applyResourceHeatmapType(
    configurations: IPictoConfiguration[],
    resourceData: Map<number, IResourcePictoData>
  ): IFinalPicto[] {
    const pictos: IFinalPicto[] = [];

    for (const config of configurations) {
      const data = resourceData.get(config.id);
      if (!data) {
        continue;
      }

      pictos.push({
        id: config.id,
        x: config.finalX,
        y: config.finalY,
        size: config.size,
        type: PictoTypeEnum.Heatmap,
        url: `images/heatmap.svg`,
        showMapIcon: data.showMapIcon,
        priority: 0,
        title: data.name,
        subtitle: data.subtitle,
        isFavorite: data.isFavorite,
        status: data.status,
        capacity: data.capacity,
        occupantsCount: data.occupantsCount,
        heatmapIntensity: data.heatmapIntensity,
      });
    }

    return pictos;
  }
}
